import React, { useEffect, useMemo, useState } from "react";
import "./admin-discount.css";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { HashLoader } from "react-spinners";
import { MdDelete, MdOutlineAdd } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa6";
import { deleteDiscountForAdmin, getAllDiscountsForAdmin } from "../../api/discount.api";
import AddDiscountModal from "../../components/modal/add-discount-modal";
import ConfirmModal from "../../components/modal/confirm-modal";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { FaSearch } from "react-icons/fa";

const ITEMS_PER_PAGE = 8;

export const AdminDiscount = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [discounts, setDiscounts] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { user } = useUser();

  const [isDeleteDiscountModalOpen, setIsDeleteDiscountModalOpen] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin - Mã giảm giá";
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async (showLoader = true) => {
    try {
      if (showLoader) setIsFetching(true);
      const res = await getAllDiscountsForAdmin();

      if (res.success) {
        setDiscounts(res.data.discounts || []);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const filteredDiscounts = useMemo(() => {
    return discounts.filter((item) => {
      const keyword = searchInput.trim().toLowerCase();
      const matchKeyword = !keyword || item.code?.toLowerCase().includes(keyword);
      const matchStatus = statusFilter === "all" ? true : statusFilter === "active" ? item.isActive : !item.isActive;
      return matchKeyword && matchStatus;
    });
  }, [discounts, searchInput, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredDiscounts.length / ITEMS_PER_PAGE));

  const paginatedDiscounts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDiscounts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDiscounts, currentPage]);

  const summary = useMemo(() => {
    return discounts.reduce(
      (acc, item) => {
        if (item.isActive) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );
  }, [discounts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchInput, statusFilter]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleDeleteDiscount = async () => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setIsDeleteDiscountModalOpen(false);
    try {
      const res = await deleteDiscountForAdmin(selectedIdToDelete);
      if (res.success) {
        showToast("Xóa mã giảm giá thành công", "success");
        setDiscounts((prev) => prev.filter((dis) => dis._id.toString() !== selectedIdToDelete));
        setSelectedIdToDelete("");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="admin-discount-page">
      <ToastNotification />
      <AddDiscountModal
        isOpen={isModalOpen}
        title={"Thêm mã giảm giá"}
        message={"Vui lòng điền đúng giá trị giảm và loại giảm giá"}
        onClose={() => setIsModalOpen(false)}
        setDiscounts={setDiscounts}
        discounts={discounts}
      />
      <ConfirmModal
        isOpen={isDeleteDiscountModalOpen}
        onConfirm={handleDeleteDiscount}
        onClose={() => setIsDeleteDiscountModalOpen(false)}
        message={"Xác nhận bạn đang xóa một mã giảm giá"}
        title={"Xóa mã giảm giá"}
      />

      <div className="admin-discount-shell">
        <div className="admin-discount-header">
          <div>
            <p className="admin-discount-breadcrumb">Quản trị / Mã giảm giá</p>
            <h1 className="admin-discount-title">Danh sách mã giảm giá</h1>
          </div>
          <div className="admin-discount-kpi">
            <div className="admin-discount-kpi-item">
              <span>Tổng mã</span>
              <strong>{discounts.length}</strong>
            </div>
            <div className="admin-discount-kpi-item">
              <span>Đang hoạt động</span>
              <strong>{summary.active}</strong>
            </div>
            <div className="admin-discount-kpi-item">
              <span>Không hoạt động</span>
              <strong>{summary.inactive}</strong>
            </div>
          </div>
        </div>

        <div className="admin-discount-toolbar">
          <div className="admin-discount-search-wrap">
            <FaSearch className="admin-discount-search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm theo code"
              className="admin-discount-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <select
            className="admin-discount-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Hoạt động</option>
            <option value="inactive">Không hoạt động</option>
          </select>

          {hasPermission(user?.role, "create:discount") && (
            <button
              type="button"
              className="admin-discount-add-button"
              onClick={() => setIsModalOpen(true)}
            >
              <MdOutlineAdd className="admin-discount-add-icon" />
              <span>Thêm mã giảm giá</span>
            </button>
          )}
        </div>

        {isFetching && discounts.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="admin-discount-table-wrap">
              <table className="admin-discount-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Code</th>
                    <th>Giá trị</th>
                    <th>Đã dùng</th>
                    <th>Tối đa</th>
                    <th>Bắt đầu</th>
                    <th>Kết thúc</th>
                    <th>Trạng thái</th>
                    <th>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDiscounts.length > 0 ? (
                    paginatedDiscounts.map((item, index) => (
                      <tr key={item?._id}>
                        <td className="admin-discount-cell-muted">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-discount-code-link"
                            onClick={() => navigate(`/super-admin/discounts/view/${item?._id}`)}
                          >
                            {item?.code}
                          </button>
                        </td>
                        <td className="admin-discount-cell-value">
                          {item?.discount_value?.toLocaleString("vi-VN") || 0}
                          {item?.discount_type === 'percentage' ? "%" : "đ"}
                        </td>
                        <td>{item?.usedCount}</td>
                        <td>{item?.limitUsage}</td>
                        <td className="admin-discount-cell-muted">{new Date(item?.start_date).toLocaleDateString("vi-VN")}</td>
                        <td className="admin-discount-cell-muted">{new Date(item?.end_date).toLocaleDateString("vi-VN")}</td>
                        <td>
                          <span className={`admin-discount-status ${item?.isActive ? "admin-discount-status-active" : "admin-discount-status-inactive"}`}>
                            {item?.isActive ? "Hoạt động" : "Không hoạt động"}
                          </span>
                        </td>
                        <td>
                          <div className="admin-discount-actions">
                            <button
                              className="admin-discount-action admin-discount-action-view"
                              onClick={() => navigate(`/super-admin/discounts/view/${item?._id}`)}
                              title="Xem chi tiết"
                              disabled={isActionLoading}
                            >
                              <FaEye />
                            </button>
                            {hasPermission(user?.role, "delete:discount") && (
                              <button
                                onClick={() => {
                                  setIsDeleteDiscountModalOpen(true);
                                  setSelectedIdToDelete(item?._id);
                                }}
                                className="admin-discount-action admin-discount-action-delete"
                                title="Xóa mã"
                                disabled={isActionLoading}
                              >
                                <MdDelete />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9}>
                        <p className="admin-discount-empty">Không có mã giảm giá phù hợp bộ lọc.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
